import { Component, inject, OnDestroy, OnInit, ViewChild, Injector } from '@angular/core';
import { isDeclinedRefusal } from 'core/api/api-model';
import { Router, RouterLink, ActivatedRoute } from '@angular/router';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Location } from '@angular/common';

import { EMPTY, firstValueFrom, Observable, of, Subscription } from 'rxjs';
import { catchError, filter, map, skip, tap, switchMap } from 'rxjs/operators';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';
import { CdkStep } from '@angular/cdk/stepper';
import {
  CustomerModel,
  PaymentMethodFormData,
  PaymentMethodMode,
  PaymentMethodModel,
  AchMethodModel,
  OneTimePaymentMethodModel,
  SelfOnboardCustomerRequest,
} from '../../../customer/customer.model';
import { PaymentCollectStepperComponent } from '../../components/payment-collect/stepper/stepper.component';
import { PaymentCollectCustomersComponent } from '../../components/payment-collect/customers/customers.component';
import { PaymentCollectPaymentMethodsComponent } from '../../components/payment-collect/payment-methods/payment-methods.component';
import { InvoiceDetailsComponent } from '../../components/payment-collect/invoice-details/invoice-details.component';
import { SummaryComponent } from '../../components/payment-collect/summary/summary.component';
import { SelectedCustomerComponent } from '../../components/payment-collect/selected-customer/selected-customer.component';
import { isMultiInvoiceModeForced } from '../../components/payment-collect/invoice-details/multi-invoice-mode';
import { OverlayService } from 'shared/services/overlay.service';
import { InvoiceDetailsInterface, InvoiceCheckoutItem, MultiInvoiceCheckoutData } from '../../payment.model';
import { TransactionsService } from '../../../transactions/services/transactions.service';
import {
  CreditApplication,
  FlashpayPayload,
  FlashpayAttachmentPayload,
  TransactionSaleLineItem,
  TransactionSalePayload,
  refundDetails,
  adjustDetails,
} from '../../../transactions/transaction.model';
import { PaymentCollectStore } from '../../store/payment-collect.store';
import { ModalComponentInterface, ModalService, MODAL_SIZE } from 'shared/components/modal/modal.service';
import { CustomerDetailsFormComponent } from '../../../customer/components/customer-details-form/customer-details-form.component';
import { BulkPaymentWizardComponent } from '../../components/bulk-payment-wizard/bulk-payment-wizard.component';
import { NachaWizardComponent } from '../../components/nacha-wizard/nacha-wizard.component';
import {
  AdvancedFlashpaySendData,
  AdvancedFlashpaySendDialogComponent,
} from '../../components/advanced-flashpay-send-dialog/advanced-flashpay-send-dialog.component';
import { CustomersService } from '../../../customer/services/customers.service';
import { NotificationService } from 'shared/services/notification.service';
import { CardDetailsFormComponent, cardDeclinedMessage } from '../../components/card-details-form/card-details-form.component';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { NgClass, NgIf } from '@angular/common';
import { AchDetailsFormComponent } from '../../components/ach-details-form/ach-details-form.component';
import { UserStore } from '../../../user/store/user.store';
import { SelfOnboardCustomerFormComponent } from '../../../customer/components/self-onboard-customer/self-onboard-customer-form.component';
import { AuthStore } from '../../../user/store/auth.store';
import { CryptService } from '../../../../shared/services/crypt.service';
import { buildOneTimePaymentAccount } from '../../utils/one-time-payment-account';
import { DEBIT_CARD_NOT_ACCEPTED, DEBIT_NOT_ACCEPTED_MESSAGE } from 'app/shared/utils/debit-card.util';

export type PaymentCollectFormType = FormGroup<{
  customer: FormControl<CustomerModel | null>;
  payment: FormControl<PaymentMethodFormData | null>;
  invoice: FormControl<InvoiceDetailsInterface | null>;
}>;

@Component({
  selector: 'app-collect',
  standalone: true,
  imports: [
    PaymentCollectStepperComponent,
    PaymentCollectCustomersComponent,
    PaymentCollectPaymentMethodsComponent,
    InvoiceDetailsComponent,
    SummaryComponent,
    SelectedCustomerComponent,

    RouterLink,
    MatButton,
    MatIcon,
    CdkStep,
    ReactiveFormsModule,
    MatTooltip,
    NgClass,
    NgIf,
    AchDetailsFormComponent,
    SelfOnboardCustomerFormComponent,
  ],
  templateUrl: './collect.component.html',
  host: {
    class: 'page page-payment-collect',
  },
})
export class CollectComponent implements OnInit, OnDestroy {
  @ViewChild('invoiceDetails') invoiceDetails!: InvoiceDetailsComponent;
  @ViewChild('stepper') stepper!: PaymentCollectStepperComponent;
  @ViewChild(PaymentCollectPaymentMethodsComponent) paymentMethodsComponent!: PaymentCollectPaymentMethodsComponent;

  readonly store = inject(PaymentCollectStore);
  readonly userStore = inject(UserStore);
  readonly authStore = inject(AuthStore);

  form: PaymentCollectFormType = new FormGroup({
    customer: new FormControl<CustomerModel | null>(null, [Validators.required]),
    payment: new FormControl<PaymentMethodFormData | null>({
      paymentMode: PaymentMethodMode.creditcard,
    }),
    invoice: new FormControl<InvoiceDetailsInterface | null>(null),
  });

  subscription = new Subscription();

  $allowNext: boolean = true;

  publicKey: string = '';

  /**
   * Set when this collect was started from one customer's invoice
   * (`?customerId=…&invoiceId=…&skipToStep=3`). The customer comes from the linking page's
   * navigation state when it passed one.
   */
  readonly entryContext: { customerId: string; invoiceId: string; customer: CustomerModel | null } | null;

  /**
   * Whether Step 1 has been shown. On a collect started from an invoice, its grid over the whole
   * customer book is only built once the user goes back to pick another customer.
   */
  customerStepOpened = false;

  // Add a flag to track if we're coming from the Add Payment Method modal
  private isFromAddCardPayment = false;
  private isFromAddAchPayment = false;

  get isFlashPay(): boolean {
    return this.form.controls.payment.value?.paymentMode == PaymentMethodMode.flashpay;
  }

  get isAchPay(): boolean {
    return this.form.controls.payment.value?.paymentMode == PaymentMethodMode.achpay;
  }

  /**
   * Whether Steps 2 and 3 offer to go back and pick another customer. Not in the views that
   * act on an existing transaction (adjust, refund, complete auth): there the customer is the
   * transaction's.
   */
  get canChangeCustomer(): boolean {
    const view = this.activatedRoute.snapshot.queryParams['view'];
    return !['adjust', 'refund', 'completeAuth'].includes(view);
  }

  get isOneTimePay(): boolean {
    return this.form.controls.payment.value?.paymentMode == PaymentMethodMode.onetimepay;
  }

  get isOneTimeCard(): boolean {
    return this.form.controls.payment.value?.paymentMode == PaymentMethodMode.onetimecard;
  }

  get paymentFormControl(): FormControl<PaymentMethodFormData | null> {
    return this.form.get('payment') as FormControl<PaymentMethodFormData | null>;
  }

  get isFlashPayEnabled(): boolean {
    const profile = this.userStore.activeProfile();
    return profile?.profileDetail?.profileAdminSettings?.enableFlashPay ?? false;
  }

  /**
   * Advanced FlashPay (`enableAdvancedFlashPay`, needs invoices): FlashPay sends the
   * customer's selected open invoices as one email instead of one typed invoice.
   */
  get isAdvancedFlashPayEnabled(): boolean {
    const s = this.userStore.activeProfile()?.profileDetail?.profileAdminSettings;
    return s?.enableAdvancedFlashPay === true && s?.enableInvoice === true;
  }

  get isAchPayEnabled(): boolean {
    const profile = this.userStore.activeProfile();
    return profile?.profileDetail?.profileAdminSettings?.enableAchPay ?? false;
  }

  get isBulkPaymentEnabled(): boolean {
    const profile = this.userStore.activeProfile();
    return profile?.profileDetail?.profileAdminSettings?.enableBulkPayment ?? false;
  }

  get isNachaWizardEnabled(): boolean {
    const profile = this.userStore.activeProfile();
    return profile?.profileDetail?.profileAdminSettings?.enableNachaWizard ?? false;
  }

  /** Gates the "self onboard customer" button. Defaults true when unset. */
  get isFlashOnboardEnabled(): boolean {
    const profile = this.userStore.activeProfile();
    return profile?.profileDetail?.profileAdminSettings?.enableFlashOnboard ?? true;
  }

  /** When true, the "add customer" button is hidden (customers are ERP-imported). */
  get isAddCustomerDisabled(): boolean {
    const profile = this.userStore.activeProfile();
    return profile?.profileDetail?.profileAdminSettings?.disableAddCustomer ?? false;
  }

  /**
   * Gates one-time / adhoc payment surfaces (the "use one-time payment" button).
   * Not allowed when the profile uses invoices but has adhoc auto-gen disabled —
   * payments must be applied to existing imported invoices.
   */
  get isAdhocInvoiceAllowed(): boolean {
    const s = this.userStore.activeProfile()?.profileDetail?.profileAdminSettings;
    if (s?.enableInvoice && !s?.enableAdhocInvoiceAutoGen) {
      return false;
    }
    return true;
  }

  // need to add onetimepay to profileAdminSettings
  // get isOneTimePayEnabled(): boolean {
  //   const profile = this.userStore.activeProfile();
  //   return profile?.profileDetail?.profileAdminSettings?.enableOneTimePay ?? false;
  // }

  constructor(
    public route: Router,
    protected activatedRoute: ActivatedRoute,
    protected overlayService: OverlayService,
    protected transactionsApiService: TransactionsService,
    protected modalService: ModalService,
    protected customerService: CustomersService,
    protected notificationService: NotificationService,
    private injector: Injector,
    private cryptService: CryptService,
    private location: Location
  ) {
    const params = this.activatedRoute.snapshot.queryParams;
    if (params['customerId'] && params['invoiceId'] && params['skipToStep'] === '3') {
      // Arriving from another layout (the Customers page), this page is built after the
      // navigation has finished, so its state is read back from the history entry.
      const state = this.route.getCurrentNavigation()?.extras.state ?? (this.location.getState() as Record<string, unknown> | null);
      const passed = state?.['customer'] as CustomerModel | undefined;
      this.entryContext = {
        customerId: params['customerId'],
        invoiceId: params['invoiceId'],
        customer: passed && passed.id === params['customerId'] ? passed : null,
      };
    } else {
      this.entryContext = null;
    }
    this.customerStepOpened = !this.entryContext;

    // Keep the wizard form's selected customer in sync when customers change (an edit via View
    // Details / Customer Editor, an onboarding). Without this, Summary and payment payloads read a
    // stale snapshot of the customer. Refetched on its own: this page no longer holds the book.
    toObservable(this.store.customersVersion)
      .pipe(
        skip(1),
        map(() => this.form.controls.customer.value?.id),
        filter((id): id is string => !!id),
        switchMap((id) => this.customerService.getCustomer(id).pipe(catchError(() => EMPTY))),
        takeUntilDestroyed()
      )
      .subscribe((fresh) => {
        const current = this.form.controls.customer.value;
        if (!current?.id || fresh?.id !== current.id) return;

        const changed =
          fresh.contactEmail !== current.contactEmail ||
          fresh.contactPhoneNumber !== current.contactPhoneNumber ||
          fresh.mobilePhoneNumber !== current.mobilePhoneNumber ||
          fresh.companyName !== current.companyName ||
          fresh.displayName !== current.displayName ||
          fresh.contactFirstName !== current.contactFirstName ||
          fresh.contactLastName !== current.contactLastName ||
          fresh.companyAccountNumber !== current.companyAccountNumber;

        if (changed) {
          // Over the current value, so fields only the list carried (payment types) survive.
          this.form.controls.customer.patchValue({ ...current, ...fresh });
        }
      });
  }

  ngOnInit(): void {
    // Reset payment mode to credit card on component init
    this.form.controls.payment.patchValue(
      {
        paymentMode: PaymentMethodMode.creditcard,
      },
      { emitEvent: false }
    ); // Prevent immediate emission
    this.paymentFormControl.updateValueAndValidity();

    // The form starts with no customer, so any accounts the store still holds are the last
    // collect's. Picking a customer (or the one an invoice link names) loads theirs.
    this.store.clearPayments();

    this.subscription.add(
      this.form.valueChanges.subscribe((result) => {
        result.customer && this.store.addCustomer(result.customer);
        result.payment && this.store.addPayment(result.payment);
        result.invoice && this.store.addInvoice(result.invoice);
      })
    );

    // ECDH session is established at login; encryptData lazily ensures it.

    // Handle pre-populated data from query params (e.g., from Collect button)
    this.handlePrePopulatedData();
  }

  /**
   * A collect started from one customer's invoice (the Collect button on an invoice): opens on
   * Step 3 with that invoice selected. The stepper index and the invoice step's inputs read this
   * from the template, so nothing waits on timers.
   */
  private handlePrePopulatedData(): void {
    if (!this.entryContext) {
      return;
    }
    const { customerId, customer } = this.entryContext;

    // The page that linked here usually has the customer already; fetch only when it did not
    // (a refresh, a pasted link).
    if (customer) {
      this.form.controls.customer.patchValue(customer);
      return;
    }

    this.customerService.getCustomer(customerId).subscribe({
      next: (fetched) => this.form.controls.customer.patchValue(fetched),
      error: (error) => {
        console.error('Error loading customer:', error);
        this.notificationService.error('Error loading customer data');
      },
    });
  }

  collectFlashPayData(): FlashpayPayload {
    const formData = this.form.getRawValue();
    const payment = formData!.payment! as PaymentMethodModel;
    const achMethod = formData!.payment!.achMethod! as AchMethodModel;
    const returnpayload = {
      flashPayId: payment.id ?? '',
      profileId: payment.customerId ?? '1111',
      paymentAmountRequested: Number(formData!.invoice!.totalAmount),
      taxAmountRequested: Number(formData!.invoice!.taxAmount ?? 0),
      shippingAmountRequested: Number(formData!.invoice!.shippingAmount ?? 0),
      referenceNumber: <string>formData!.invoice!.invoiceNumber,
      referenceAmountRequested: Number(formData!.invoice!.totalAmount),
      costOfCapitalAmountRequested: Number(formData!.invoice!.capitalAmount ?? 0),
      customerEmailAddress: formData!.customer!.contactEmail ?? '',
      mobilePhoneNumber: formData!.customer!.contactPhoneNumber ?? undefined,
      freelyCustomerId: String(formData!.customer!.id),
      customerComments: '1111',
      autoSend: true,

      // description: achMethod?.description ?? '',
      // accountType: achMethod?.accountType ?? '',
      // account: achMethod?.account ?? '',
      // abaRoute: achMethod?.abaRoute ?? '',
      // accountHolderName: achMethod?.accountHolderName ?? '',
    };
    if (returnpayload.mobilePhoneNumber == '') {
      delete returnpayload.mobilePhoneNumber;
    }

    // Forward any invoice attachments from the invoice-details step
    const invoiceData = formData!.invoice! as InvoiceDetailsInterface;
    if (invoiceData?.attachments && invoiceData.attachments.length > 0) {
      (returnpayload as FlashpayPayload).attachments = invoiceData.attachments as FlashpayAttachmentPayload[];
    }

    return returnpayload;
  }

  /** The gateway paymentAccount for a one-time entry (CARD or ACH); shared with Quick Pay. */
  private buildOneTimePaymentAccount(payment: PaymentMethodModel | null | undefined): Record<string, unknown> {
    const requireCVV = this.userStore.activeProfile()?.profileDetail?.profileAdminSettings?.requireCVV ?? true;
    return buildOneTimePaymentAccount(payment, requireCVV);
  }

  async collectPaymentData(): Promise<TransactionSalePayload> {
    console.log('collectPaymentData', this.form.getRawValue()?.payment?.paymentMode);

    const formData = this.form.getRawValue();
    const payment = formData?.payment?.paymentMethod;
    const achMethod = formData!.payment!.achMethod! as AchMethodModel;
    const oneTimePaymentMethod = formData!.payment!.oneTimePaymentMethod! as OneTimePaymentMethodModel;

    // Base payload
    const payload: TransactionSalePayload = {
      customerId: formData?.customer?.id,
      reference: formData?.invoice?.invoiceNumber ?? '',
      amount: Number(formData?.invoice?.totalAmount ?? 0),
      taxAmount: Number(formData?.invoice?.taxAmount ?? 0),
      shippingAmount: Number(formData?.invoice?.shippingAmount ?? 0),
      costOfCapitalAmount: Number(formData?.invoice?.capitalAmount ?? 0),
    };

    // Add session token to the request
    const authToken = this.authStore.accessToken(); // Use access token

    console.log('Session token for backend (first 10 chars):', authToken ? authToken.substring(0, 10) + '...' : 'none');

    // Add payment specific fields
    if (formData?.payment?.paymentMode === PaymentMethodMode.achpay) {
      // ACH payment
      return {
        ...payload,
        tokenId: payment?.id ?? '',
      };
    } else if (formData?.payment?.paymentMode === PaymentMethodMode.onetimepay) {
      // ONE TIME PAY — CARD or ACH, discriminated by the built paymentMethod.
      const paymentAccountData = this.buildOneTimePaymentAccount(payment);

      const paymentAccountJson = JSON.stringify(paymentAccountData);

      console.log('paymentAccountJson', paymentAccountJson);
      const encryptedPaymentAccount = await this.cryptService.encryptData(paymentAccountJson);

      return {
        ...payload,
        customerName: oneTimePaymentMethod?.customerName || '',
        customerReferenceNumber: oneTimePaymentMethod?.customerReferenceNumber || '',
        saveCustomer: oneTimePaymentMethod?.saveCustomer || false,
        saveToken: oneTimePaymentMethod?.saveToken || false,
        encryptedPaymentAccount: encryptedPaymentAccount,
      };
    } else if (formData?.payment?.paymentMode === PaymentMethodMode.onetimecard) {
      // ONE TIME CARD — selected customer is preserved; account is not tokenized.
      const paymentAccountData = this.buildOneTimePaymentAccount(payment);
      const encryptedPaymentAccount = await this.cryptService.encryptData(JSON.stringify(paymentAccountData));

      return {
        ...payload,
        customerName: formData?.customer?.displayName ?? '',
        saveCustomer: false,
        saveToken: false,
        encryptedPaymentAccount,
      };
    } else {
      // Credit card payment (tokenized)
      const tokenPayload: TransactionSalePayload = {
        ...payload,
        tokenId: payment?.id ?? '',
      };
      if (payment?.cvv) {
        tokenPayload.cvv = payment.cvv;
      }
      if (payment?.streetAddress) {
        tokenPayload.avsAddress = payment.streetAddress;
      }
      if (payment?.zipCode) {
        tokenPayload.avsZip = payment.zipCode;
      }

      return tokenPayload;
    }
  }

  onSubmit() {
    const formData = this.form.getRawValue();
    const paymentMode = formData?.payment?.paymentMode ?? PaymentMethodMode.creditcard;
    switch (paymentMode) {
      case PaymentMethodMode.flashpay:
        this.onSubmitFlashPay();
        break;

      case PaymentMethodMode.achpay:
        this.onSubmitDefault();
        break;

      case PaymentMethodMode.onetimepay:
        this.onSubmitDefault();
        break;

      case PaymentMethodMode.onetimecard:
        this.onSubmitDefault();
        break;

      case PaymentMethodMode.creditcard:
        this.onSubmitDefault();
        break;

      default:
        this.onSubmitDefault();
        break;
    }
  }

  onSubmitFlashPay() {
    if (!this.form.valid) {
      return;
    }

    if (this.isAdvancedFlashPayEnabled) {
      this.openAdvancedFlashPaySend();
      return;
    }

    if (!this.form.controls.customer.value?.contactEmail || this.form.controls.customer.value?.contactEmail == '') {
      this.modalService.openModal({
        body: 'Customer email is required for FlashPay',
        dismiss: null,
        confirm: {
          label: 'Close',
          action: (modal: ModalComponentInterface): void => {
            modal.close();
          },
        },
      });
      return;
    }

    const overrideEnabled =
      this.userStore.activeProfile()?.profileDetail?.profileAdminSettings?.enableCustomerPaymentMethodOverride ?? false;
    if (overrideEnabled) {
      const customerSettings = this.form.controls.customer.value?.customerSettings;
      const allowCard = customerSettings?.acceptCreditCardPayment !== false;
      const allowAch = customerSettings?.acceptACHPayment !== false;
      if (!allowCard && !allowAch) {
        this.modalService.openModal({
          body: 'This customer is not allowed to accept payments. Update customer settings to allow Card and/or ACH payments before sending FlashPay.',
          dismiss: null,
          confirm: {
            label: 'Close',
            action: (modal: ModalComponentInterface): void => {
              modal.close();
            },
          },
        });
        return;
      }
    }

    const flashpayPayload = this.collectFlashPayData();
    this.overlayService.showOverlay();
    this.transactionsApiService
      .createFlashpay(flashpayPayload)
      .pipe(
        tap(() => {
          this.overlayService.hideOverlay();
          if (this.userStore.activeProfile()?.profileDetail?.profileAdminSettings?.enableAutoReceipt) {
            this.notificationService.success(
              'Success. Your auto receipts are enabled for this profile. If an email exists for the customer, an email receipt will be sent.',
              'ok',
              5000
            );
          } else {
            this.notificationService.success('Success.', 'ok', 3000);
          }
        })
      )
      .subscribe({
        next: (flashpay) => {
          //console.log('flashpay', flashpay);
          this.route.navigate(['/payment/collect-success'], {
            queryParams: { view: 'flashpay', id: flashpay.flashPayId },
          });
        },
        error: (error) => {
          this.overlayService.hideOverlay();
          this.modalService.openModal({
            body: 'Flashpay creation failed, please try again',
            dismiss: null,
            confirm: {
              label: 'Close',
              action: (modal: ModalComponentInterface): void => {
                modal.close();
              },
            },
          });
        },
      });
  }

  /**
   * Advanced FlashPay: confirm recipients, expiry and attachments for the
   * invoices ticked on the invoice step, then send them as one email.
   */
  openAdvancedFlashPaySend() {
    const customer = this.form.controls.customer.value;
    const invoiceData = this.form.getRawValue().invoice as InvoiceDetailsInterface;
    const items = invoiceData?.multiInvoiceData?.invoices ?? [];
    if (!customer?.id || items.length === 0) {
      this.notificationService.error('Select at least one invoice to send.');
      return;
    }

    const data: AdvancedFlashpaySendData = {
      customerName: customer.companyName || customer.displayName || '',
      contactEmail: customer.contactEmail,
      invoices: items.map((item) => ({
        invoiceRef: item.invoice.invoiceNumber,
        dueDate: item.invoice.dueDate,
        amount: Number(item.outstandingBalance ?? item.paymentAmount),
      })),
    };

    this.modalService.openModal({
      title: 'Send invoices with FlashPay',
      body: AdvancedFlashpaySendDialogComponent,
      size: MODAL_SIZE.md,
      data,
      confirm: {
        label: 'Send',
        action: (modal: ModalComponentInterface, dialog: AdvancedFlashpaySendDialogComponent) => {
          const payload = dialog.payload();
          if (!payload) {
            this.notificationService.error(dialog.problem ?? 'Check the form and try again.');
            return;
          }
          this.overlayService.showOverlay();
          this.transactionsApiService.createAdvancedFlashpay(String(customer.id), payload).subscribe({
            next: (flashpay) => {
              this.overlayService.hideOverlay();
              modal.close();
              this.notificationService.success(`FlashPay sent with ${payload.invoiceRefs.length} invoice(s).`, 'ok', 3000);
              this.route.navigate(['/payment/collect-success'], {
                queryParams: { view: 'flashpay', id: flashpay.flashPayId },
              });
            },
            error: (error) => {
              this.overlayService.hideOverlay();
              this.notificationService.error(this.describeAdvancedFlashPayError(error), 'ok', 8000);
            },
          });
        },
      },
      dismiss: {
        label: 'Cancel',
        action: (modal: ModalComponentInterface) => modal.close(),
      },
    });
  }

  /** FTE's refusal, with each invoice it refused named. */
  private describeAdvancedFlashPayError(error: any): string {
    const body = error?.data ?? error?.error ?? error;
    const details: { invoiceRef: string; message: string }[] = Array.isArray(body?.details) ? body.details : [];
    const listed = details
      .filter((d) => d && typeof d === 'object' && d.invoiceRef)
      .map((d) => `${d.invoiceRef}: ${d.message}`)
      .join('; ');
    const message = body?.message || error?.message || 'FlashPay could not be sent, please try again.';
    return listed ? `${message} ${listed}` : message;
  }

  /**
   * Shared error handling for the payment/refund/adjust submit calls.
   *
   * If the gateway declined an actual transaction there is a transactionId on the
   * response, so we route to the error result page (unchanged behaviour). Otherwise
   * — e.g. a 400 validation error like "Cumulative refund amount ($477.92) exceeds
   * original transaction amount ($238.96)." — there is no transaction to display, so
   * we surface the backend message via a snackbar instead of failing silently.
   * (Previously this read `error.data.transactionResponse.transactionId` directly,
   * which threw a TypeError on such errors and left the user with no feedback.)
   */
  /**
   * A payment FTE refused before charging but tells as a DECLINE — a duplicate payment, or a repeat
   * against an invoice the first payment already settled. It moves forward to the declined result
   * like any decline, rather than leaving the operator on the wizard with a toast; there is no
   * transaction to show, so the result page uses its `declined` view. Returns true when it routed.
   */
  private routeToDeclinedRefusal(error: any): boolean {
    if (!isDeclinedRefusal(error?.data)) return false;
    this.route.navigate(['/payment/collect-success'], { queryParams: { view: 'declined' } });
    return true;
  }

  private handleSubmitError(error: any): void {
    console.log('error', error);
    this.overlayService.hideOverlay();

    const failedTransactionId = error?.data?.transactionResponse?.transactionId;
    if (failedTransactionId) {
      this.route.navigate(['/payment/collect-success'], {
        queryParams: { view: 'error', transactionId: failedTransactionId },
      });
      return;
    }

    if (this.routeToDeclinedRefusal(error)) return;

    // FTE refuses a payment naming a NotPayable invoice before charging anything — reachable
    // when the list on screen predates the import that dropped the invoice. The transport
    // message ("Http failure response … 409 Conflict") says nothing; FTE's detail names it.
    // INVOICE_NOT_PAYABLE also covers an invoice already paid in full ("…is already paid — a second
    // payment was refused"); PAYMENT_IN_PROGRESS is a second submit landing while the first is still
    // being processed. In every case nothing was charged.
    // A DUPLICATE payment is not handled here on purpose: FTE answers it as an ordinary decline
    // (402 "Declined"), and the generic branch below shows exactly that.
    const refusals: Record<string, string> = {
      INVOICE_NOT_PAYABLE: 'An invoice in this payment is not payable.',
      PAYMENT_IN_PROGRESS: 'A payment to this account or invoice is already being processed.',
      // blockDebitCards: the profile refuses debit cards; the card was never charged.
      [DEBIT_CARD_NOT_ACCEPTED]: DEBIT_NOT_ACCEPTED_MESSAGE,
    };
    if (refusals[error?.data?.message]) {
      const detail = Array.isArray(error.data.details) ? error.data.details[0] : null;
      this.notificationService.error(
        detail || refusals[error.data.message],
        'ok',
        8000
      );
      return;
    }

    // The credit-side counterpart: a credit the ERP stopped listing (`credit_not_usable`), one
    // that moved, or one that no longer covers what was allocated. Same reasoning — the 409's
    // transport message says nothing, and FTE puts the reason in `details.problems`.
    const creditProblem = this.creditProblemMessage(error);
    if (creditProblem) {
      this.notificationService.error(creditProblem, 'ok', 8000);
      return;
    }

    const message = error?.message || error?.data?.message || 'Something went wrong. Please try again.';
    this.notificationService.error(message, 'ok', 8000);
  }

  /**
   * FTE's own reason for refusing a credit application (409 `CREDIT_APPLICATION_INVALID`), or
   * null when this is not that error. `details.problems[]` carries one entry per pairing it
   * would not accept — `credit_not_usable` among them — and the first is the one to show.
   */
  private creditProblemMessage(error: any): string | null {
    if (error?.data?.message !== 'CREDIT_APPLICATION_INVALID') {
      return null;
    }
    const details = error?.data?.details;
    const problems = Array.isArray(details?.problems) ? details.problems : Array.isArray(details) ? details : [];
    const first = problems[0];
    const message = typeof first === 'string' ? first : first?.message;
    return message || 'A credit in this payment cannot be applied: the ERP no longer lists it as open.';
  }

  async onSubmitDefault() {
    const view = this.activatedRoute.snapshot.queryParams['view'];
    console.log('View', view);
    // if (!this.form.valid) {
    //   return;
    // }
    const formData = this.form.getRawValue();

    // Check if multi-invoice mode
    if (formData.invoice?.isMultiInvoiceMode && formData.invoice?.multiInvoiceData) {
      await this.processMultiInvoicePayment(formData);
      return;
    }

    const salePayload = await this.collectPaymentData();

    // if preauth checked, process preauth
    if (formData.invoice && formData.invoice.enablePreauth) {
      this.overlayService.showOverlay();
      this.transactionsApiService
        .processPreauth(salePayload)
        .pipe(
          tap(() => {
            this.overlayService.hideOverlay();
            if (this.userStore.activeProfile()?.profileDetail?.profileAdminSettings?.enableAutoReceipt) {
              this.notificationService.success(
                'Success. Your auto receipts are enabled for this profile. If an email exists for the customer, an email receipt will be sent.',
                'ok',
                5000
              );
            } else {
              this.notificationService.success('Success.', 'ok', 3000);
            }
          })
        )
        .subscribe({
          next: (transaction) => {
            this.store.addTransaction(transaction);
            this.route.navigate(['/payment/collect-success'], {
              queryParams: { view: 'preAuth', transactionId: transaction.transactionId },
            });
          },
          error: (error) => {
            this.handleSubmitError(error);
          },
        });
      return;
    } else if (formData.invoice && view === 'completeAuth') {
      this.overlayService.showOverlay();
      const transactionId = this.activatedRoute.snapshot.queryParams['transactionId'];
      const customerId = this.activatedRoute.snapshot.queryParams['customerId'];
      const amount = Number(formData.invoice.invoiceTotalAmount);
      this.transactionsApiService
        .completePreauth(transactionId, {
          customerId: customerId,
          amount: amount,
          reference: formData.invoice.invoiceNumber,
        })
        .pipe(
          tap(() => {
            this.overlayService.hideOverlay();
            if (this.userStore.activeProfile()?.profileDetail?.profileAdminSettings?.enableAutoReceipt) {
              this.notificationService.success(
                'Success. Your auto receipts are enabled for this profile. If an email exists for the customer, an email receipt will be sent.',
                'ok',
                5000
              );
            } else {
              this.notificationService.success('Success.', 'ok', 3000);
            }
          })
        )
        .subscribe({
          next: (transaction) => {
            this.store.addTransaction(transaction);
            this.route.navigate(['/payment/collect-success'], {
              queryParams: { view: 'completeAuth', transactionId: transactionId },
            });
          },
          error: (error) => {
            this.handleSubmitError(error);
          },
        });
      return;
    } else if (formData.invoice && view === 'refund') {
      this.overlayService.showOverlay();

      let customerId = this.activatedRoute.snapshot.queryParams['customerId'];
      if (customerId == '') {
        customerId = undefined;
      }

      const refundDetailsPayload: refundDetails = {
        //customerId: customerId,
        //originalTransactionId: this.activatedRoute.snapshot.queryParams['transactionId'],
        // relatedTransactionId: this.activatedRoute.snapshot.queryParams['transactionId'],

        //originalPaymentId: formData.invoice.invoiceNumber, // type says freelyPaymentId
        //refundAmount: Number(formData.invoice.totalAmount),
        //newTotal: Number(formData.invoice.invoiceTotalAmount),
        //newShipping: Number(formData.invoice.shippingAmount),
        //newTax: Number(formData.invoice.taxAmount),
        //newCostOfCapital: Number(formData.invoice.capitalAmount),
        // notes: '1111',
        // comments: '1111',
        totalRequestedRefundAmount: Number(formData.invoice.totalAmount),
        amounts: {
          amount: Number(formData.invoice.totalAmount),
          //taxAmount: Number(formData.invoice.taxAmount),
        },
        //     receiptRequest?: IReceiptRequest;
      };

      const transactionId = this.activatedRoute.snapshot.queryParams['transactionId'];

      console.log('refundDetailsPayload', refundDetailsPayload);

      //   export interface IRefundRequest {
      //     customerId: string;
      //     token?: string;
      //     relatedTransactionId: string;
      //     relatedFreelyPaymentId?: string;
      //     totalRequestedAmount: number;
      //     amounts?: IAmountRequest;
      //     receiptRequest?: IReceiptRequest;
      //     comments?: string;
      //     amounts?: IAmountRequest;
      //     receiptRequest?: IReceiptRequest;
      //     comments?: string;
      // }

      // const customerId = this.activatedRoute.snapshot.queryParams['customerId'];
      // const amount = Number(formData.invoice.invoiceTotalAmount);
      // const refundAmount = Number(formData.invoice.invoiceTotalAmount);

      //old payload
      // {
      //   customerId: customerId,
      //   relatedTransactionId: transactionId,
      //   amounts: {
      //     amount: amount,
      //   },
      //   totalRequestedAmount: amount,
      // }

      this.transactionsApiService
        .refund(transactionId, refundDetailsPayload)
        .pipe(
          tap(() => {
            this.overlayService.hideOverlay();
            if (this.userStore.activeProfile()?.profileDetail?.profileAdminSettings?.enableAutoReceipt) {
              this.notificationService.success(
                'Success. Your auto receipts are enabled for this profile. If an email exists for the customer, an email receipt will be sent.',
                'ok',
                5000
              );
            } else {
              this.notificationService.success('Success.', 'ok', 3000);
            }
          })
        )
        .subscribe({
          next: (originalTransactionId) => {
            console.log('refund success', originalTransactionId);
            this.route.navigate(['/payment/collect-success'], {
              queryParams: { view: 'refund', transactionId: (originalTransactionId as any).transactionId },
            });
          },
          error: (error) => {
            this.handleSubmitError(error);
          },
        });
      return;
    } else if (formData.invoice && view === 'adjust') {
      this.overlayService.showOverlay();

      let customerId = this.activatedRoute.snapshot.queryParams['customerId'];
      if (customerId == '') {
        customerId = undefined;
      }

      let capitalAmount: number | undefined = Math.round(Number(formData.invoice.capitalAmount ?? 0) * 100) / 100;
      let taxAmount: number | undefined = Math.round(Number(formData.invoice.taxAmount ?? 0) * 100) / 100;
      let shippingAmount: number | undefined = Math.round(Number(formData.invoice.shippingAmount ?? 0) * 100) / 100;

      if (capitalAmount === 0) {
        capitalAmount = undefined;
      }

      if (taxAmount === 0) {
        taxAmount = undefined;
      }

      if (shippingAmount === 0) {
        shippingAmount = undefined;
      }

      let invoiceTotalAmount = Math.round(Number(formData.invoice.invoiceTotalAmount ?? 0) * 100) / 100;

      let lineItemAmount = invoiceTotalAmount - (capitalAmount ?? 0) - (taxAmount ?? 0) - (shippingAmount ?? 0);

      const adjustDetailsPayload: adjustDetails = {
        customerId: customerId,
        paymentReference: formData.invoice.invoiceNumber,
        amount: invoiceTotalAmount,
        lineItemAmount: lineItemAmount,
        costOfCapitalAmount: capitalAmount,
        taxAmount: taxAmount,
        shippingAmount: shippingAmount,

        // //originalTransactionId: this.activatedRoute.snapshot.queryParams['transactionId'],
        // relatedTransactionId: this.activatedRoute.snapshot.queryParams['transactionId'],
        // //originalPaymentId: formData.invoice.invoiceNumber, // type says freelyPaymentId
        // //refundAmount: Number(formData.invoice.totalAmount),
        // //newTotal: Number(formData.invoice.invoiceTotalAmount),
        // //newShipping: Number(formData.invoice.shippingAmount),
        // //newTax: Number(formData.invoice.taxAmount),
        // //newCostOfCapital: Number(formData.invoice.capitalAmount),
        // // notes: '1111',
        // // comments: '1111',
        // totalRequestedAmount: Number(formData.invoice.totalAmount),
        // amounts: {
        //   amount: Number(formData.invoice.totalAmount),
        //   //taxAmount: Number(formData.invoice.taxAmount),
        // },
        // //     receiptRequest?: IReceiptRequest;
      };

      const transactionId = this.activatedRoute.snapshot.queryParams['transactionId'];

      console.log('adjustDetailsPayload', adjustDetailsPayload);

      //   export interface IRefundRequest {
      //     customerId: string;
      //     token?: string;
      //     relatedTransactionId: string;
      //     relatedFreelyPaymentId?: string;
      //     totalRequestedAmount: number;
      //     amounts?: IAmountRequest;
      //     receiptRequest?: IReceiptRequest;
      //     comments?: string;
      //     amounts?: IAmountRequest;
      //     receiptRequest?: IReceiptRequest;
      //     comments?: string;
      // }

      // const customerId = this.activatedRoute.snapshot.queryParams['customerId'];
      // const amount = Number(formData.invoice.invoiceTotalAmount);
      // const refundAmount = Number(formData.invoice.invoiceTotalAmount);

      //old payload
      // {
      //   customerId: customerId,
      //   relatedTransactionId: transactionId,
      //   amounts: {
      //     amount: amount,
      //   },
      //   totalRequestedAmount: amount,
      // }

      this.transactionsApiService
        .adjust(transactionId, adjustDetailsPayload)
        .pipe(
          tap(() => {
            this.overlayService.hideOverlay();
            if (this.userStore.activeProfile()?.profileDetail?.profileAdminSettings?.enableAutoReceipt) {
              this.notificationService.success(
                'Success. Your auto receipts are enabled for this profile. If an email exists for the customer, an email receipt will be sent.',
                'ok',
                5000
              );
            } else {
              this.notificationService.success('Success.', 'ok', 3000);
            }
          })
        )
        .subscribe({
          next: (originalTransactionId) => {
            console.log('adjust success', originalTransactionId);
            this.route.navigate(['/payment/collect-success'], {
              queryParams: { view: 'adjust', transactionId: (originalTransactionId as any).transactionId },
            });
          },
          error: (error) => {
            this.handleSubmitError(error);
          },
        });
      return;
    } else if (formData.invoice && view === 'onetimepay') {
      console.log('ONE TIME PAY salepayload', salePayload);

      this.overlayService.showOverlay();
      this.transactionsApiService
        .transactionSale(salePayload)
        .pipe(
          tap(() => {
            this.overlayService.hideOverlay();
            if (this.userStore.activeProfile()?.profileDetail?.profileAdminSettings?.enableAutoReceipt) {
              this.notificationService.success(
                'Success. Your auto receipts are enabled for this profile. If an email exists for the customer, an email receipt will be sent.',
                'ok',
                5000
              );
            } else {
              this.notificationService.success('Success.', 'ok', 3000);
            }
          })
        )
        .subscribe({
          next: (transaction) => {
            this.store.addTransaction(transaction);
            if (formData?.payment?.paymentMode === PaymentMethodMode.achpay) {
              this.route.navigate(['/payment/collect-success'], {
                queryParams: { view: 'saleAch', transactionId: transaction.transactionId },
              });
            } else if (formData?.payment?.paymentMode === PaymentMethodMode.creditcard) {
              this.route.navigate(['/payment/collect-success'], {
                queryParams: { view: 'saleCard', transactionId: transaction.transactionId },
              });
            } else if (formData?.payment?.paymentMode === PaymentMethodMode.onetimepay) {
              this.route.navigate(['/payment/collect-success'], {
                queryParams: { view: 'onetimepay', transactionId: transaction.transactionId },
              });
            }
            // if onetimepay, check if saveCustomer is true
            if (
              formData?.payment?.paymentMode === PaymentMethodMode.onetimepay &&
              formData?.payment?.oneTimePaymentMethod?.saveCustomer
            ) {
              this.store.reloadCustomers();
              //console.log('reload customers');
            }
          },
          error: (error) => {
            this.handleSubmitError(error);
          },
        });
      return;
    }

    // else do sale
    //console.log('sale formdata', formData);
    console.log('Sale payload', salePayload);

    this.overlayService.showOverlay();
    this.transactionsApiService
      .sale(salePayload)
      .pipe(
        tap(() => {
          this.overlayService.hideOverlay();
          if (this.userStore.activeProfile()?.profileDetail?.profileAdminSettings?.enableAutoReceipt) {
            this.notificationService.success(
              'Success. Your auto receipts are enabled for this profile. If an email exists for the customer, an email receipt will be sent.',
              'ok',
              5000
            );
          } else {
            this.notificationService.success('Success.', 'ok', 3000);
          }
        })
      )
      .subscribe({
        next: (transaction) => {
          this.store.addTransaction(transaction);
          if (formData?.payment?.paymentMode === PaymentMethodMode.achpay) {
            this.route.navigate(['/payment/collect-success'], {
              queryParams: { view: 'saleAch', transactionId: transaction.transactionId },
            });
          } else {
            this.route.navigate(['/payment/collect-success'], {
              queryParams: { view: 'saleCard', transactionId: transaction.transactionId },
            });
          }
        },
        error: (error) => {
          console.log('error', error);
          this.overlayService.hideOverlay();

          if (this.routeToDeclinedRefusal(error)) return;

          // Surface the backend message (e.g. "Payment token does not belong
          // to the specified customer") instead of silently swallowing it.
          const message = error?.message || 'Payment failed. Please try again.';
          this.notificationService.error(message, 'ok', 8000);

          // Only navigate to the error-success screen when the backend actually
          // produced a transaction (e.g. gateway decline). Pre-charge rejections
          // like the 403 ownership check have no transactionResponse — staying
          // on the wizard lets the user correct the selection and retry.
          const transactionId = error?.data?.transactionResponse?.transactionId;
          if (transactionId) {
            this.route.navigate(['/payment/collect-success'], {
              queryParams: { view: 'error', transactionId },
            });
          }
        },
      });
  }

  openAddCustomerModal() {
    this.$allowNext = false;
    this.modalService.openModal({
      title: 'Customer Information',
      body: CustomerDetailsFormComponent,
      data: {},
      confirm: {
        label: 'Save',
        action: (modal, customerFormComponent: CustomerDetailsFormComponent) => {
          const form = customerFormComponent.form;
          if (!form.valid) {
            form.markAllAsTouched();
            return;
          }
          const formData = form.getRawValue();
          const customerModel: CustomerModel = {
            contactFirstName: <string>formData!.contactFirstName,
            contactLastName: <string>formData!.contactLastName,
            displayName: <string>formData!.displayName,
            companyName: <string>formData!.companyName,
            companyAccountNumber: <string>formData!.companyAccountNumber,
            contactEmail: <string>formData!.contactEmail,
            contactPhoneNumber: <string>formData!.businessPhone + ' ' + <string>formData!.businessPhoneExt,
            mobilePhoneNumber: <string>formData!.mobilePhoneNumber,
            notes: <string>formData!.notes,
          };

          if (!customerModel.contactEmail) {
            delete customerModel.contactEmail;
          }
          if (customerModel.contactEmail == '') {
            delete customerModel.contactEmail;
          }
          if (customerModel.mobilePhoneNumber == '') {
            delete customerModel.mobilePhoneNumber;
          }
          if (customerModel.contactPhoneNumber == '') {
            delete customerModel.contactPhoneNumber;
          }
          if (customerModel.contactFirstName == '') {
            delete customerModel.contactFirstName;
          }
          if (customerModel.contactLastName == '') {
            delete customerModel.contactLastName;
          }

          this.customerService
            .createCustomer(customerModel)
            .pipe(tap(() => this.overlayService.showOverlay()))
            .subscribe({
              next: (customer) => {
                this.store.reloadCustomers();
                this.$allowNext = true;
                modal.close();
                this.overlayService.hideOverlay();
                this.notificationService.success('Customer created successfully', 'ok', 500);
                this.form.controls.customer.patchValue(customer);
              },
              error: (error) => {
                this.overlayService.hideOverlay();
                this.notificationService.error(error.message, 'ok', 2000);
              },
            });
        },
      },
      dismiss: (modal: any) => {
        modal.close();
        this.$allowNext = true;
      },
    });
  }

  openBulkPaymentWizard() {
    this.modalService.openModal({
      title: 'Bulk Payment Wizard',
      body: BulkPaymentWizardComponent,
      size: MODAL_SIZE.xxlg,
      data: {},
      dismiss: {
        label: 'Close',
        action: (modal: ModalComponentInterface) => {
          modal.close();
        },
      },
      confirm: null,
    });
  }

  openNachaWizard() {
    this.modalService.openModal({
      title: 'Process NACHA File',
      body: NachaWizardComponent,
      size: MODAL_SIZE.xxlg,
      data: {},
      dismiss: {
        label: 'Close',
        action: (modal: ModalComponentInterface) => {
          modal.close();
        },
      },
      confirm: null,
    });
  }

  openSelfOnboardCustomerModal() {
    // Create the modal config
    const modalConfig: any = {
      title: 'Self-Onboard Customer',
      body: SelfOnboardCustomerFormComponent,
      data: {},
      confirm: {
        label: 'Save',
        action: (
          modal: ModalComponentInterface,
          selfOnboardCustomerFormComponent: SelfOnboardCustomerFormComponent
        ) => {
          const form = selfOnboardCustomerFormComponent.form;
          if (!form.valid) {
            form.markAllAsTouched();
            return;
          }
          const formData = form.getRawValue();

          const selfOnboardCustomerRequest: SelfOnboardCustomerRequest = {
            customerEmail: <string>formData!.customerEmail,
            customerAccountNumber: <string>formData!.customerAccountNumber,
          };

          this.customerService
            .createSelfOnboardCustomerLink(selfOnboardCustomerRequest)
            .pipe(tap(() => this.overlayService.showOverlay()))
            .subscribe({
              next: (selfOnboardCustomerResponse) => {
                modal.close();
                this.overlayService.hideOverlay();
                this.$allowNext = true;

                this.notificationService.success('Flash onboard invite has been sent to customer successfully');
                this.store.reloadCustomers();
                console.log('selfOnboardCustomerResponse', selfOnboardCustomerResponse);
              },
              error: (error) => {
                this.overlayService.hideOverlay();
                this.notificationService.error(error.message);
              },
            });
        },
      },
      dismiss: {
        label: 'Cancel',
        action: (modal: ModalComponentInterface) => {
          modal.close();
          this.$allowNext = true;

          // Reset the flag when canceling
          this.isFromAddCardPayment = false;
          this.isFromAddAchPayment = false;
        },
      },
    };

    // Only add the "Switch to Add Payment" button if coming from Add Payment
    if (this.isFromAddCardPayment) {
      modalConfig.additionalButtons = [
        {
          label: 'Switch to Add Payment',
          class: 'mat-primary',
          position: 'top',
          action: (modal: ModalComponentInterface) => {
            modal.close();
            setTimeout(() => this.openAddPaymentMethodModal(), 100);
          },
        },
      ];
    }

    // Open the modal with the configured options
    this.modalService.openModal(modalConfig);
  }

  /** @param paymentType which kind of account to ask for — the add-card or add-ACH dialog it was opened from. */
  openRequestFlashAddPaymentMethodModal(paymentType: 'card' | 'ach') {
    const customer = this.store.customer();

    if (!customer) {
      this.notificationService.error('Please select a customer first');
      return;
    }

    const initialData: SelfOnboardCustomerRequest = {
      customerAccountNumber: customer.companyAccountNumber,
      customerEmail: customer.contactEmail,
      type: 'paymentflashupdate', // Using 'paymentmethod' type for adding a new payment method
      customerId: customer.id,
      paymentType: paymentType,
    };

    // Create the modal config
    const modalConfig: any = {
      title: 'Request Customer to Add a Payment Method',
      body: SelfOnboardCustomerFormComponent, // Using the form component
      data: initialData,
      confirm: {
        label: 'Send Request',
        action: (
          modal: ModalComponentInterface,
          selfOnboardCustomerFormComponent: SelfOnboardCustomerFormComponent
        ) => {
          const form = selfOnboardCustomerFormComponent.form;
          if (!form.valid) {
            form.markAllAsTouched();
            return;
          }
          const formData = form.getRawValue();

          const selfOnboardCustomerRequest: SelfOnboardCustomerRequest = {
            customerEmail: formData.customerEmail || '',
            customerAccountNumber: formData.customerAccountNumber || '',
            type: 'paymentflashadd', // Using 'paymentmethod' type for adding a new payment method
            customerId: customer.id,
            paymentType: paymentType,
          };

          this.customerService
            .createSelfOnboardCustomerLink(selfOnboardCustomerRequest)
            .pipe(tap(() => this.overlayService.showOverlay()))
            .subscribe({
              next: () => {
                modal.close();
                this.overlayService.hideOverlay();
                this.$allowNext = true;

                this.notificationService.success('Customer payment method request sent successfully');
                this.isFromAddCardPayment = false;
                this.isFromAddAchPayment = false;

                this.persistTypedEmailIfChanged(selfOnboardCustomerRequest.customerEmail);
              },
              error: (error) => {
                this.overlayService.hideOverlay();
                this.notificationService.error(error.message);
              },
            });
        },
      },
      dismiss: {
        label: 'Cancel',
        action: (modal: ModalComponentInterface) => {
          modal.close();
          this.$allowNext = true;

          // Reset the flag when canceling
          this.isFromAddCardPayment = false;
          this.isFromAddAchPayment = false;
        },
      },
    };

    // Only add the "add payment method" button if coming from Add Payment
    if (this.isFromAddCardPayment) {
      modalConfig.additionalButtons = [
        {
          label: 'add payment method',
          class: 'mat-primary',
          position: 'top',
          action: (modal: ModalComponentInterface) => {
            modal.close();
            setTimeout(() => this.openAddPaymentMethodModal(), 100);
          },
        },
      ];
    } else if (this.isFromAddAchPayment) {
      modalConfig.additionalButtons = [
        {
          label: 'add payment method',
          class: 'mat-primary',
          position: 'top',
          action: (modal: ModalComponentInterface) => {
            modal.close();
            setTimeout(() => this.openAddAchPaymentMethodModal(), 100);
          },
        },
      ];
    }

    // Open the modal with the configured options
    this.modalService.openModal(modalConfig);
  }

  openAddPaymentMethodModal() {
    this.$allowNext = false;
    this.isFromAddAchPayment = false;
    this.isFromAddCardPayment = true; // Set the flag when opening Add Payment modal
    this.modalService.openModal({
      title: 'Add Payment Card',
      body: CardDetailsFormComponent,
      data: {},
      confirm: {
        label: 'Save',
        action: (modal: ModalComponentInterface, cardDetailsFormComponent: CardDetailsFormComponent) => {
          const form = cardDetailsFormComponent.form;
          if (!form.valid || !cardDetailsFormComponent.isFullyValidated) {
            form.markAllAsTouched();
            if (!form.valid) {
              const failingFields: string[] = [];
              if (form.controls.account.invalid) failingFields.push('Card Number');
              if (form.controls.expirationDate.invalid) {
                if (form.controls.expirationDate.hasError('expirationDateInvalid')) failingFields.push('Expiration Date (expired)');
                else if (form.controls.expirationDate.hasError('expirationDateTooFar')) failingFields.push('Expiration Date (too far in future)');
                else failingFields.push('Expiration Date');
              }
              if (form.controls.cvv.invalid) failingFields.push('CVV');
              if (form.controls.cardHolderName.invalid) failingFields.push('Card Holder Name');
              if (form.controls.streetAddress.invalid) failingFields.push('Street Address');
              if (form.controls.zipCode.invalid) failingFields.push('Zip Code');
              cardDetailsFormComponent.invalidMessage = failingFields.length
                ? `Please fix: ${failingFields.join(', ')}`
                : 'Please complete all required fields';
            } else if (!cardDetailsFormComponent.isFullyValidated) {
              cardDetailsFormComponent.invalidMessage = 'Card must be verified before saving';
            }
            return;
          }

          const customer = this.store.customer();
          if (!customer) {
            this.notificationService.error('Please select a customer');
            return;
          }

          const paymentMethodModel = cardDetailsFormComponent.getFormData();

          console.log('paymentMethodModel', paymentMethodModel);

          this.cryptService
            .encryptData(paymentMethodModel)
            .then((encryptedPaymentMethod) => {
              const encryptedPaymentMethodModel: PaymentMethodModel = {
                encryptedPaymentAccount: encryptedPaymentMethod,
              };

              this.customerService
                .createPaymentMethod(<string>customer!.id, encryptedPaymentMethodModel)
                .pipe(
                  tap(() => this.overlayService.showOverlay()),
                  switchMap((response) => {
                    const setDefault$: Observable<unknown> = paymentMethodModel.isDefault
                      ? this.customerService.setDefaultPaymentMethod(<string>customer!.id, <string>response.id)
                      : of(null);
                    return setDefault$.pipe(map(() => response.id));
                  })
                )
                .subscribe({
                  next: (createdId) => {
                    // A card added here is the one about to be charged
                    this.paymentMethodsComponent?.selectWhenLoaded(createdId);
                    this.store.reloadPaymentMethods();
                    modal.close();
                    this.$allowNext = true;
                    this.overlayService.hideOverlay();
                    this.notificationService.success('Payment card created successfully');
                  },
                  error: (error) => {
                    this.overlayService.hideOverlay();
                    const details = error?.data?.details || {};
                    const verbiage = error?.data?.message || '';
                    const suffix = verbiage ? ` — ${verbiage}` : '';

                    if (details.validCVV === false) {
                      cardDetailsFormComponent.invalidMessage = `Invalid CVV${suffix}`;
                      cardDetailsFormComponent.isCvvInvalid = true;
                      cardDetailsFormComponent.hasCvvBeenChecked = true;
                      cardDetailsFormComponent.showIgnoreCVV = true;
                      cardDetailsFormComponent.form.controls.cvv.updateValueAndValidity();
                      cardDetailsFormComponent.cvvInput.nativeElement.focus();
                    } else if (details.validAVSStreet === false || details.validAVSZip === false) {
                      cardDetailsFormComponent.invalidMessage = `Invalid Address/ZIP${suffix}`;
                      cardDetailsFormComponent.isAvsInvalid = true;
                      cardDetailsFormComponent.isStreetInvalid = details.validAVSStreet === false;
                      cardDetailsFormComponent.isZipInvalid = details.validAVSZip === false;
                      cardDetailsFormComponent.hasStreetBeenChecked = true;
                      cardDetailsFormComponent.hasZipBeenChecked = true;
                      if (cardDetailsFormComponent.isStreetInvalid) cardDetailsFormComponent.showIgnoreStreet = true;
                      if (cardDetailsFormComponent.isZipInvalid) cardDetailsFormComponent.showIgnoreZip = true;
                      cardDetailsFormComponent.form.controls.streetAddress.updateValueAndValidity();
                      cardDetailsFormComponent.form.controls.zipCode.updateValueAndValidity();
                      cardDetailsFormComponent.addressInput.nativeElement.focus();
                    } else if (details.validCard === false) {
                      cardDetailsFormComponent.invalidMessage = cardDeclinedMessage(verbiage);
                      cardDetailsFormComponent.isCardInvalid = true;
                      cardDetailsFormComponent.form.controls.account.updateValueAndValidity();
                      cardDetailsFormComponent.accountInput.nativeElement.focus();
                    } else if (verbiage) {
                      cardDetailsFormComponent.invalidMessage = verbiage;
                      cardDetailsFormComponent.isCardInvalid = true;
                      cardDetailsFormComponent.form.controls.account.updateValueAndValidity();
                    } else {
                      cardDetailsFormComponent.invalidMessage =
                        'An error occurred while creating the payment card. Please try again.';
                      cardDetailsFormComponent.form.updateValueAndValidity();
                    }
                  },
                });
            })
            .catch((error) => {
              console.error('Encryption failed:', error);
              this.notificationService.error('Failed to encrypt payment card data');
              this.overlayService.hideOverlay();
            });
        },
      },
      dismiss: {
        label: 'Cancel',
        action: (modal: ModalComponentInterface) => {
          modal.close();
          this.$allowNext = true;
          this.isFromAddCardPayment = false; // Reset the flag when canceling
          this.isFromAddAchPayment = false;
        },
      },
      additionalButtons: [
        {
          label: 'request payment method',
          class: 'mat-primary',
          position: 'top',
          action: (modal: ModalComponentInterface) => {
            modal.close();
            setTimeout(() => this.openRequestFlashAddPaymentMethodModal('card'), 100);
          },
        },
      ],
    });
  }

  openAddAchPaymentMethodModal() {
    this.$allowNext = false;
    this.isFromAddCardPayment = false;
    this.isFromAddAchPayment = true; // Set the flag when opening Add Payment modal
    this.modalService.openModal({
      title: 'Add ACH Account',
      body: AchDetailsFormComponent,
      data: {},
      confirm: {
        label: 'Save',
        action: (modal: ModalComponentInterface, achDetailsFormComponent: AchDetailsFormComponent) => {
          const form = achDetailsFormComponent.form;
          if (!form.valid) {
            form.markAllAsTouched();
            return;
          }

          const customer = this.store.customer();
          if (!customer) {
            this.notificationService.error('Please select a customer');
            return;
          }

          const achMethodModel = achDetailsFormComponent.getFormData();

          this.cryptService
            .encryptData(achMethodModel)
            .then((encryptedData) => {
              // Now encryptedData is a string
              achMethodModel.encryptedPaymentAccount = encryptedData;

              const encryptedPaymentMethodModel: AchMethodModel = {
                encryptedPaymentAccount: encryptedData,
              };

              this.customerService
                .createACHPaymentMethod(<string>customer!.id, encryptedPaymentMethodModel)
                .pipe(
                  tap(() => this.overlayService.showOverlay()),
                  switchMap((response) => {
                    const setDefault$: Observable<unknown> = achMethodModel.isDefault
                      ? this.customerService.setDefaultPaymentMethod(<string>customer!.id, <string>response.id)
                      : of(null);
                    return setDefault$.pipe(map(() => response.id));
                  })
                )
                .subscribe({
                  next: (createdId) => {
                    // An account added here is the one about to be charged — and choosing an
                    // ACH account is what makes this an ACH payment
                    this.paymentMethodsComponent?.selectWhenLoaded(createdId);
                    this.store.reloadPaymentMethods();
                    modal.close();
                    this.$allowNext = true;
                    this.overlayService.hideOverlay();
                    this.notificationService.success('ACH account added successfully');
                  },
                  error: (error) => {
                    this.overlayService.hideOverlay();
                    this.notificationService.error('Failed to add ACH account: ' + error.message);
                  },
                });
            })
            .catch((error) => {
              console.error('Encryption failed:', error);
              this.notificationService.error('Failed to encrypt ACH account data');
              this.overlayService.hideOverlay();
            });
        },
      },
      dismiss: {
        label: 'Cancel',
        action: (modal: ModalComponentInterface) => {
          modal.close();
          this.$allowNext = true;
          this.isFromAddAchPayment = false; // Reset the flag when canceling
          this.isFromAddCardPayment = false;
        },
      },
      additionalButtons: [
        {
          label: 'request payment method',
          class: 'mat-primary',
          position: 'top',
          action: (modal: ModalComponentInterface) => {
            modal.close();
            setTimeout(() => this.openRequestFlashAddPaymentMethodModal('ach'), 100);
          },
        },
      ],
    });
  }

  toggleFlashPay() {
    const payment = this.form.controls.payment;
    const currentMode = payment.getRawValue()?.paymentMode;

    const newMode =
      currentMode === PaymentMethodMode.flashpay ? PaymentMethodMode.creditcard : PaymentMethodMode.flashpay;

    payment.patchValue({
      paymentMode: newMode,
    });
    // payment.updateValueAndValidity();
    // this.store.reloadPaymentMethods(newMode);

    this.paymentMethodsComponent?.deselectPaymentMethod();

    return false;
  }

  toggleOnetimePay() {
    const payment = this.form.controls.payment;
    // Leaving one-time pay returns to choosing a saved account, whose rail then sets the mode
    const newMode = this.isOneTimePay ? PaymentMethodMode.creditcard : PaymentMethodMode.onetimepay;

    payment.patchValue({
      paymentMode: newMode,
    });
    payment.updateValueAndValidity();
    this.store.reloadPaymentMethods();

    return false;
  }

  toggleOnetimeCard() {
    const payment = this.form.controls.payment;
    const newMode = this.isOneTimeCard ? PaymentMethodMode.creditcard : PaymentMethodMode.onetimecard;

    payment.patchValue({
      paymentMode: newMode,
      paymentMethod: null,
      oneTimePaymentMethod: null,
    });
    payment.updateValueAndValidity();

    // Reload saved accounts when returning to them so the list is fresh.
    if (newMode === PaymentMethodMode.creditcard) {
      this.store.reloadPaymentMethods();
    }

    this.paymentMethodsComponent?.deselectPaymentMethod();
    // A one-time card holds Next until its details validate (`oneTimePaymentValidated`). Saved
    // accounts have no such signal — the payment form's own validity gates Next — so leaving
    // one-time card mode must reopen the gate, or Next stays disabled whatever card is chosen.
    this.$allowNext = newMode !== PaymentMethodMode.onetimecard;

    return false;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private persistTypedEmailIfChanged(typedEmail: string | undefined | null): void {
    const currentCustomer = this.form.controls.customer.value;
    if (!currentCustomer?.id || !typedEmail) return;
    if (typedEmail === currentCustomer.contactEmail) return;

    this.customerService
      .updateCustomer(currentCustomer.id, { ...currentCustomer, contactEmail: typedEmail })
      .subscribe({
        next: () => this.store.reloadCustomers(),
        error: (err) => this.notificationService.error(err.message),
      });
  }

  getInvoiceDetailsStepTitle(): string {
    const view = this.activatedRoute.snapshot.queryParams['view'];
    if (view === 'completeAuth') {
      return 'What are the authorization details?';
    }
    if (this.form?.get('invoice')?.value?.enablePreauth) {
      return 'What are the preauthorization details?';
    }
    if (view === 'onetimepay') {
      return 'What are the one-time payment invoice details?';
    }
    // Invoice mode is the customer's whole balance: invoices, credits and the account. A forced
    // mode is decided from settings + view, never read off the child: its ngOnInit switches
    // mid-render, after this title was checked (NG0100).
    if (
      isMultiInvoiceModeForced(this.userStore.activeProfile()?.profileDetail?.profileAdminSettings, view) ||
      this.invoiceDetails?.isMultiInvoiceMode
    ) {
      return 'Customer Balance';
    }
    return 'What are the invoice details?';
  }

  skipToInvoiceDetails(): void {
    this.toggleOnetimePay();
    this.stepper.selectedStep = 'payment';
    // clear customer form
    this.form.controls.customer.patchValue(null);
    // add view=onetime to queryParams
    this.route.navigate(['/payment/collect'], { queryParams: { view: 'onetimepay' } });
  }

  resetToCustomerStep(): void {
    this.toggleOnetimePay();
    this.stepper.selectedStep = 'customer';
    // this.form.controls.customer.patchValue(null);
    // this.form.controls.payment.patchValue(null);
    this.route.navigate(['/payment/collect'], { queryParams: { view: null } });
    // this.form.reset();
  }

  goBack(): void {
    this.route.navigate(['/history'], { queryParams: { view: 'open' } });
  }

  // Multi-invoice payment processing
  /**
   * Process a multi-invoice payment as a SINGLE transaction. Each
   * invoice becomes a line item on the same charge so the customer
   * sees one card/ACH debit instead of N separate ones, and the
   * backend produces a single FreelyPayment with N invoice events
   * (one per line item) plus the auto-generated CoC invoice (if
   * applicable). Sum of line-item amounts + CoC = total charge.
   */
  private async processMultiInvoicePayment(formData: any): Promise<void> {
    const multiInvoiceData = formData.invoice.multiInvoiceData;
    const customer = formData.customer;
    const paymentMethod = formData.payment;

    if (!multiInvoiceData || !customer || !paymentMethod) {
      this.notificationService.error('Missing required data for multi-invoice payment');
      return;
    }

    const invoiceItems: InvoiceCheckoutItem[] = multiInvoiceData.invoices ?? [];
    const onAccountAmount = Number(multiInvoiceData.onAccountAmount ?? 0);
    if (invoiceItems.length === 0 && !(onAccountAmount > 0)) {
      this.notificationService.error('No invoices selected');
      return;
    }

    // Credit covers everything and nothing goes on the account: there is nothing to
    // charge, so the credit is applied without one.
    const creditApplications = multiInvoiceData.creditApplications ?? [];
    if (Number(multiInvoiceData.totalAmount ?? 0) <= 0 && creditApplications.length > 0) {
      await this.applyCreditsWithoutCharge(customer.id, creditApplications, invoiceItems.length);
      return;
    }

    this.overlayService.showOverlay();

    try {
      const payload = await this.buildMultiInvoicePayload(
        customer,
        paymentMethod,
        invoiceItems,
        multiInvoiceData,
      );

      const transaction = await this.transactionsApiService.sale(payload).toPromise();
      this.overlayService.hideOverlay();

      if (!transaction) {
        throw new Error('Transaction returned undefined');
      }

      this.store.addTransaction(transaction);

      const count = invoiceItems.length;
      this.notificationService.success(
        count > 0
          ? `Successfully processed ${count} invoice${count === 1 ? '' : 's'} in a single transaction`
          : 'Payment applied to the account',
        'ok',
        3000,
      );

      this.route.navigate(['/payment/collect-success'], {
        queryParams: {
          view: 'multiInvoice',
          successCount: count,
          failCount: 0,
          transactionId: (transaction as { transactionId?: string })?.transactionId,
          ...(onAccountAmount > 0 ? { onAccount: onAccountAmount } : {}),
        },
      });
    } catch (error) {
      console.error('Multi-invoice payment failed:', error);
      // A declined charge goes to the result page; a request FTE refused before charging —
      // credit no longer available, more on the account than the customer owes — says why
      // and leaves the operator where they can fix it.
      this.handleSubmitError(error);
    }
  }

  /** Credit covers the whole payment: FTE records the application as a $0 payment. */
  private async applyCreditsWithoutCharge(
    customerId: string,
    applications: CreditApplication[],
    invoiceCount: number,
  ): Promise<void> {
    this.overlayService.showOverlay();
    try {
      const result = await firstValueFrom(this.customerService.applyCredits(customerId, { applications }));
      this.overlayService.hideOverlay();
      this.notificationService.success('Credit applied — nothing was charged.', 'ok', 3000);
      this.route.navigate(['/payment/collect-success'], {
        queryParams: {
          view: 'creditApplied',
          successCount: invoiceCount,
          id: result.freelyPaymentId,
        },
      });
    } catch (error: any) {
      this.overlayService.hideOverlay();
      // Same 409 as the charge path — `CREDIT_APPLICATION_INVALID` names the credit and why,
      // where `error.data.message` alone would show the bare code.
      const message =
        this.creditProblemMessage(error) ||
        error?.data?.message ||
        error?.message ||
        'The credit could not be applied.';
      this.notificationService.error(message, 'ok', 8000);
    }
  }

  /**
   * Build the single-charge payload for paying N invoices at once.
   * The customer pays one consolidated amount (sum of invoice
   * portions + CoC); each invoice becomes its own line item so the
   * backend can apply the right portion to the right invoice. Sum
   * invariant: `lineItems[*].amount + costOfCapitalAmount == amount`.
   */
  private async buildMultiInvoicePayload(
    customer: CustomerModel,
    paymentMethod: PaymentMethodFormData,
    invoiceItems: InvoiceCheckoutItem[],
    multiInvoiceData?: MultiInvoiceCheckoutData,
  ): Promise<TransactionSalePayload> {
    const payment = paymentMethod.paymentMethod;
    const oneTimePaymentMethod = paymentMethod.oneTimePaymentMethod;

    const round2 = (n: number) => Math.round(n * 100) / 100;

    // What the charge puts on each invoice: its payment less any credit applied to it.
    // An invoice credit covers entirely has no cash line — the credit pairing carries it.
    const cashOf = (item: InvoiceCheckoutItem) => round2(item.cashAmount ?? item.paymentAmount ?? 0);
    const cashItems = invoiceItems.filter((item) => cashOf(item) > 0);
    const onAccountAmount = round2(Number(multiInvoiceData?.onAccountAmount ?? 0));

    // Cash charged, before CoC: invoice cash plus the amount put on the account.
    const subtotal = round2(cashItems.reduce((sum, item) => sum + cashOf(item), 0) + onAccountAmount);

    // Single CoC for the whole charge (already computed by the
    // checkout step). Apportioning across line items is unnecessary —
    // the backend treats it as a single top-level fee that lands on
    // its own auto-generated CoC invoice.
    const cocAmount =
      multiInvoiceData?.capitalCostEnabled && multiInvoiceData?.capitalCostAmount
        ? round2(multiInvoiceData.capitalCostAmount)
        : 0;

    const totalAmount = round2(subtotal + cocAmount);

    const totalTax = round2(
      cashItems.reduce((sum, item) => sum + (item.invoice.taxAmount || 0), 0),
    );
    const totalShipping = round2(
      cashItems.reduce((sum, item) => sum + (item.invoice.shippingAmount || 0), 0),
    );

    const lineItems: TransactionSaleLineItem[] = cashItems.map((item) => ({
      accountingReference: item.invoice.invoiceNumber,
      amount: cashOf(item),
      taxAmount: item.invoice.taxAmount || 0,
      shippingAmount: item.invoice.shippingAmount || 0,
    }));
    if (onAccountAmount > 0) {
      lineItems.push({ amount: onAccountAmount, onAccount: true });
    }

    // Base payload. `reference` mirrors the first invoice number so
    // single-invoice readers (gateway descriptors, batch line) still
    // see something meaningful; the authoritative breakdown lives in
    // `lineItems`.
    const creditApplications = multiInvoiceData?.creditApplications ?? [];
    const payload: TransactionSalePayload = {
      customerId: customer.id,
      amount: totalAmount,
      reference: invoiceItems[0]?.invoice.invoiceNumber ?? 'On account',
      taxAmount: totalTax,
      shippingAmount: totalShipping,
      costOfCapitalAmount: cocAmount,
      lineItems,
      ...(creditApplications.length > 0 ? { creditApplications } : {}),
    };

    if (paymentMethod.paymentMode === PaymentMethodMode.achpay) {
      return { ...payload, tokenId: payment?.id ?? '' };
    } else if (paymentMethod.paymentMode === PaymentMethodMode.onetimepay) {
      // ONE TIME PAY - need to encrypt payment data (CARD or ACH)
      const paymentAccountData = this.buildOneTimePaymentAccount(payment);

      const paymentAccountJson = JSON.stringify(paymentAccountData);
      const encryptedPaymentAccount = await this.cryptService.encryptData(paymentAccountJson);

      return {
        ...payload,
        customerName: oneTimePaymentMethod?.customerName || customer.companyName || '',
        customerReferenceNumber:
          oneTimePaymentMethod?.customerReferenceNumber || customer.companyAccountNumber || '',
        saveCustomer: oneTimePaymentMethod?.saveCustomer || false,
        encryptedPaymentAccount,
      };
    } else {
      // Tokenized credit card
      return {
        ...payload,
        tokenId: payment?.id ?? '',
        cvv: payment?.cvv ?? '',
        avsAddress: payment?.streetAddress ?? '',
        avsZip: payment?.zipCode ?? '',
      };
    }
  }
}
